<template>
	<van-calendar :type="props.type"
				  :default-date="pickerValue"
				  :color="props.color ?? $config.public.colors.primary"
				  :min-date="props.minDate"
				  :max-date="props.maxDate"
				  :first-day-of-week="1"
				  allow-same-day
				  :poppable="false"
				  :show-confirm="false"
				  :show-title="false"
				  @confirm="onConfirm">
		<template #subtitle="{text, date}">
			{{ date ? $date(date.toISOString()).format('MMMM YYYY') : text }}
		</template>
		<template #month-title="{date}">
			{{ $date(date.toISOString()).format('MMMM YYYY') }}
		</template>
	</van-calendar>
</template>

<script setup lang="ts">
import {ref, watch} from "vue";
import type {CalendarType} from 'vant';
import {deepEqual} from "@spoferan/spoferan-ts-core";
import {useNuxtApp} from "#app";
import 'vant/es/calendar/style';

const props = withDefaults(defineProps<{
	modelValue?: string | string[],
	color?: string,
	type?: CalendarType,

	// Defaults to Today
	minDate?: Date,

	// Defaults to six months after today
	maxDate?: Date
}>(), {
	type: 'single'
});

const emit = defineEmits(['update:modelValue']);

const {$date} = useNuxtApp();

const submitValue = ref(props.modelValue);
const pickerValue = ref(formatDateStringsToDates(props.modelValue));
watch(() => props.modelValue, (newValue, oldValue) => {
	if (!deepEqual(newValue, oldValue)) {
		submitValue.value = newValue;
	}
});
watch(submitValue, (newValue, oldValue) => {
	if (!deepEqual(newValue, oldValue)) {
		pickerValue.value = formatDateStringsToDates(newValue);
		emit('update:modelValue', newValue);
	}
});

function onConfirm(dates: Date | Date[]) {
	submitValue.value = formatDatesToDateStrings(dates);
}

function formatDatesToDateStrings(dates: Date | Date[]): string | string[] {
	return Array.isArray(dates) ? dates.map(date => formatDateToDateString(date)) : formatDateToDateString(dates)
}

function formatDateToDateString(date: Date) {
	return $date(date.toISOString()).format('YYYY-MM-DD');
}

function formatDateStringsToDates(dateStrings: string | string[]): Date | Date[] {
	return Array.isArray(dateStrings) ? dateStrings.map(date => formatDateStringToDate(date)) : formatDateStringToDate(dateStrings)
}

function formatDateStringToDate(dateString: string) {
	return $date(dateString).toDate();
}

</script>